/**
 * Google Places Address Autocomplete Component
 */
import React, { useState, useEffect, useRef } from 'react';
import { MapPin } from 'lucide-react';
import config from 'config';

const GOOGLE_MAPS_API_KEY = config.GOOGLE_MAPS_API_KEY;

// Load Google Maps script for Places Autocomplete
const loadGoogleMapsScript = () => {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.maps && window.google.maps.places) {
      resolve(window.google);
      return;
    }
    const existingScript = document.getElementById('google-maps-script-modal');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.google));
      return;
    }
    const script = document.createElement('script');
    script.id = 'google-maps-script-modal';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&language=fr`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = () => reject(new Error('Failed to load Google Maps'));
    document.head.appendChild(script);
  });
};

const AddressAutocomplete = ({ value, onChange, placeholder, onSelect, testId }) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const onSelectRef = useRef(onSelect);
  const [isLoaded, setIsLoaded] = useState(false);

  // Keep refs updated via effect
  useEffect(() => {
    onChangeRef.current = onChange;
    onSelectRef.current = onSelect;
  });

  useEffect(() => {
    let mounted = true;
    const initAutocomplete = async () => {
      try {
        const google = await loadGoogleMapsScript();
        if (!mounted || !inputRef.current) return;
        
        // Don't reinitialize if already set up
        if (autocompleteRef.current) return;
        
        autocompleteRef.current = new google.maps.places.Autocomplete(inputRef.current, {
          componentRestrictions: { country: 'fr' },
          fields: ['formatted_address', 'geometry', 'place_id', 'name', 'types'],
          types: ['geocode', 'establishment'],
        });

        autocompleteRef.current.addListener('place_changed', () => {
          const place = autocompleteRef.current.getPlace();
          if (place && place.geometry) {
            const address = place.formatted_address || place.name;
            if (onChangeRef.current) onChangeRef.current(address);
            if (onSelectRef.current) {
              onSelectRef.current({
                address,
                latitude: place.geometry.location.lat(),
                longitude: place.geometry.location.lng(),
                place_id: place.place_id,
                types: place.types || []
              });
            }
          }
        });
        setIsLoaded(true);
      } catch (err) {
        console.error('Google Maps init error:', err);
      }
    };
    initAutocomplete();
    return () => { mounted = false; };
  }, []); // Empty deps - only init once

  const handleInputChange = (e) => {
    onChange(e.target.value);
  };

  return (
    <div className="relative">
      <MapPin className="absolute left-2.5 sm:left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#C9A24D] z-10 pointer-events-none" />
      <input
        ref={inputRef}
        type="text"
        value={value || ''}
        onChange={handleInputChange}
        placeholder={isLoaded ? placeholder : "Chargement..."}
        className="w-full h-9 sm:h-10 pl-8 sm:pl-10 pr-2.5 sm:pr-3 bg-[#1F1F1F] border border-[#C9A24D]/30 rounded-md text-[#E5E5E5] placeholder:text-[#E5E5E5]/40 focus:border-[#C9A24D] focus:outline-none focus:ring-1 focus:ring-[#C9A24D]/30 transition-colors text-xs sm:text-sm"
        data-testid={testId}
        autoComplete="off"
      />
    </div>
  );
};

export default AddressAutocomplete;
